import { Button, Calendar, Flex, Modal, Spin, Typography } from "antd";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { getSlotsByDate } from "./apis/getSlots";
import "./styles/styles.css";
import { BookingStatus, Status } from "Constants/statusEnum";
import { OpenToMentor } from "Constants/roleEnum";

const { Text } = Typography;

const getSlotDescription = (bookingStatus) => {
  switch (bookingStatus) {
    case BookingStatus.BOOKED:
      return "Booked by user";
    case BookingStatus.OPENED:
      return "Opened";
    case BookingStatus.REQUESTED:
      return "Slot Confirmation Requested";
    case BookingStatus.CHECKEDOUT:
      return "Awaiting Payment from User";
    default:
      return "";
  }
};

export const MyCalendar = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedSlot, setSelectedSlot] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["getSlots", currentDate?.format("YYYY-MM")],
    queryFn: () =>
      getSlotsByDate({
        start: currentDate?.utc()?.startOf("month")?.toISOString(),
        end: currentDate
          ?.utc()
          ?.startOf("month")
          ?.add(1, "month")
          ?.add(7, "day")
          ?.toISOString(),
      }),
  });

  const slots = useMemo(() => {
    if (!isLoading && data) return data?.data?.slots || [];
  }, [data, isLoading]);

  const deduceSlotColor = (slot) => {
    if (slot.status === Status.DECLINED) {
      return "lightgrey";
    }
    if (slot.bookingStatus === BookingStatus.BOOKED) {
      return "#80e280";
    }
    if (slot.bookingStatus === BookingStatus.OPENED) {
      return "yellow";
    }
    if (
      slot.bookingStatus === BookingStatus.REQUESTED ||
      slot.bookingStatus === BookingStatus.CHECKEDOUT
    ) {
      return "orange";
    }

    return "grey";
  };

  // Group slots by date (for fast lookup in dateCellRender)
  const slotMap = useMemo(() => {
    if (!slots) return {};
    const map = {};
    for (const slot of slots) {
      const startTime = dayjs.utc(slot.start);
      const localDate = startTime.local();
      const dateKey = dayjs(localDate).format("YYYY-MM-DD");
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push({
        ...slot,
        statusColor: deduceSlotColor(slot),
      });
    }
    return map;
  }, [slots]);

  const dateCellRender = (date) => {
    const key = date.format("YYYY-MM-DD");
    const listData = slotMap[key] || [];

    return (
      <div className="calendar-slot-container">
        {listData.map((item, idx) => (
          <div
            key={idx}
            className="calendar-slot"
            onClick={() => setSelectedSlot(item)}
            style={{
              backgroundColor: item.statusColor,
            }}
          >
            {item.name ||
              `${dayjs.utc(item.start).local().format("HH:mm")} - ${dayjs
                .utc(item.end)
                .local()
                .format("HH:mm")} ${OpenToMentor}`}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Flex align="center" justify="center">
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Flex
      justify="center"
      align="center"
      wrap
      className="calendar-container"
      style={{ maxHeight: "100%" }}
    >
      <Calendar
        className="calendar"
        value={currentDate}
        onPanelChange={(value) => setCurrentDate(value?.startOf("day"))}
        cellRender={dateCellRender}
      />

      <Modal
        title="Slot Details"
        open={!!selectedSlot}
        onCancel={() => setSelectedSlot(null)}
        footer={
          selectedSlot?.bookingStatus === BookingStatus.BOOKED &&
          selectedSlot?.zoomJoinUrl ? (
            <Button
              className="join-button"
              type="primary"
              onClick={() => {
                window.open(
                  selectedSlot?.zoomJoinUrl,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              Join Meeting
            </Button>
          ) : null
        }
      >
        {selectedSlot && (
          <>
            <Text strong>
              Start Time:{" "}
              <span className="calendar-modal-text">
                {dayjs.utc(selectedSlot.start).local().format("HH:mm")}
              </span>
            </Text>
            <br />
            <Text strong>
              End Time:
              <span className="calendar-modal-text">
                {dayjs.utc(selectedSlot.end).local().format("HH:mm")}
              </span>
            </Text>
            <br />
            <Text strong>
              Name:{" "}
              <span className="calendar-modal-text">
                {selectedSlot.name || OpenToMentor}
              </span>
            </Text>
            <br />
            <Text strong>
              Booking Status:{" "}
              <span className="calendar-modal-text">
                {selectedSlot?.status !== Status.DECLINED
                  ? getSlotDescription(selectedSlot?.bookingStatus)
                  : "Declined Meeting"}
              </span>
            </Text>
            <br />
          </>
        )}
      </Modal>
    </Flex>
  );
};
